/**
 * /profile — refonte « bonnes affaires » du 2026-09-14.
 *
 * Même parcours qu'avant, sous la coque commune : alertes Telegram,
 * informations personnelles, double authentification.
 *
 * 2026-10-22 : le bloc « Mon offre » et la résiliation sont retirés — plus
 * aucun abonnement n'est vendu ni actif. Les routes /api/subscriptions
 * restent côté serveur.
 *
 * Retirés :
 *   - l'encadré « Copiez votre Chat ID et collez-le ci-dessous » : il décrivait
 *     l'ancienne liaison manuelle, remplacée par un code à usage unique le
 *     2026-08-16, et contredisait le bloc juste en dessous ;
 *   - le bloc « Informations du compte » : email, nom d'utilisateur, offre et
 *     ancienneté sont dans l'en-tête ;
 *   - les couleurs écrites en dur (#0f1720, #7BE0A5, #159247, #B81E1E).
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Page from '../components/Layout/Page';
import BandeauAffaires from '../components/Affaires/BandeauAffaires';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import './ProfilePage.css';

function Message({ m }) {
  if (!m?.texte) return null;
  return <div className={`zk-alerte ${m.ok ? 'ok' : 'erreur'}`} role="status">{m.texte}</div>;
}

function CodeSixChiffres({ id, valeur, onChange }) {
  return (
    <input
      id={id} className="zk-prf-code" type="text" inputMode="numeric" autoComplete="one-time-code" maxLength={6}
      placeholder="000000" value={valeur} onChange={(e) => onChange(e.target.value.replace(/\D/g, '').slice(0, 6))}
    />
  );
}

export default function ProfilePage() {
  const { user } = useAuth();

  /* ── Informations personnelles ── */
  const [form, setForm] = useState({ full_name: user?.full_name || '', phone: user?.phone || '' });
  const [enregistrement, setEnregistrement] = useState(false);
  const [msgInfos, setMsgInfos] = useState(null);

  async function enregistrer(e) {
    e.preventDefault();
    setEnregistrement(true);
    setMsgInfos(null);
    try {
      // `telegram_chat_id` n'est JAMAIS envoyé d'ici : il est posé par /lier côté
      // bot. L'envoyer depuis un état local périmé effacerait une liaison faite
      // entre-temps dans Telegram.
      await api.put('/api/users/me', form);
      setMsgInfos({ ok: true, texte: 'Profil mis à jour.' });
    } catch {
      setMsgInfos({ ok: false, texte: 'Erreur lors de la sauvegarde. Réessayez.' });
    } finally {
      setEnregistrement(false);
    }
  }

  /* ── Liaison Telegram par code à usage unique ──
     Le code est généré côté compte et consommé côté chat : ce croisement prouve
     que les deux appartiennent à la même personne. */
  const [telegramLie, setTelegramLie] = useState(!!user?.telegram_chat_id);
  const [codeTelegram, setCodeTelegram] = useState(null);
  const [telegramChargement, setTelegramChargement] = useState(false);
  const [msgTelegram, setMsgTelegram] = useState(null);

  async function genererCode() {
    setTelegramChargement(true);
    setMsgTelegram(null);
    try {
      setCodeTelegram((await api.post('/api/users/me/telegram/link-code')).data);
    } catch {
      setMsgTelegram({ ok: false, texte: 'Impossible de générer le code. Réessayez.' });
    } finally {
      setTelegramChargement(false);
    }
  }

  async function delierTelegram() {
    try {
      await api.delete('/api/users/me/telegram');
      setTelegramLie(false);
      setCodeTelegram(null);
      setMsgTelegram({ ok: true, texte: 'Compte Telegram délié — vous ne recevrez plus d’alertes.' });
    } catch {
      setMsgTelegram({ ok: false, texte: 'Impossible de délier le compte Telegram.' });
    }
  }

  /* ── Double authentification (TOTP) ──
     « Activer » → POST /2fa/setup (clé + QR) → l'utilisateur scanne → saisit un
     code → POST /2fa/enable. Tant que /enable n'a pas répondu, totp_enabled
     reste à 0 : un abandon en cours de route ne verrouille pas le compte. */
  const [config2fa, setConfig2fa] = useState(null);
  const [actif2fa, setActif2fa] = useState(!!user?.totp_enabled);
  const [code2fa, setCode2fa] = useState('');
  const [occupe2fa, setOccupe2fa] = useState(false);
  const [desactivation2fa, setDesactivation2fa] = useState(false);
  const [msg2fa, setMsg2fa] = useState(null);

  useEffect(() => { setActif2fa(!!user?.totp_enabled); }, [user?.totp_enabled]);
  useEffect(() => { setTelegramLie(!!user?.telegram_chat_id); }, [user?.telegram_chat_id]);

  async function demarrer2fa() {
    setOccupe2fa(true);
    setMsg2fa(null);
    try {
      setConfig2fa((await api.post('/api/auth/2fa/setup')).data);
      setCode2fa('');
    } catch (err) {
      setMsg2fa({ ok: false, texte: err?.response?.data?.detail || 'Impossible de démarrer la configuration.' });
    } finally {
      setOccupe2fa(false);
    }
  }

  async function envoyer2fa(e, chemin, apres) {
    e.preventDefault();
    if (code2fa.length !== 6) { setMsg2fa({ ok: false, texte: 'Le code doit contenir 6 chiffres.' }); return; }
    setOccupe2fa(true);
    try {
      await api.post(chemin, { code: code2fa });
      setCode2fa('');
      apres();
    } catch (err) {
      setMsg2fa({ ok: false, texte: err?.response?.data?.detail || 'Code invalide. Réessayez.' });
    } finally {
      setOccupe2fa(false);
    }
  }

  const confirmer2fa = (e) => envoyer2fa(e, '/api/auth/2fa/enable', () => {
    setConfig2fa(null);
    setActif2fa(true);
    setMsg2fa({ ok: true, texte: 'Double authentification activée. Un code vous sera demandé à chaque connexion.' });
  });

  const desactiver2fa = (e) => envoyer2fa(e, '/api/auth/2fa/disable', () => {
    setActif2fa(false);
    setDesactivation2fa(false);
    setMsg2fa({ ok: true, texte: 'Double authentification désactivée.' });
  });

  const membreDepuis = user?.created_at
    ? new Date(user.created_at).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })
    : null;

  return (
    <Page
      largeur="etroite"
      titre={user?.full_name || user?.username || 'Mon profil'}
      chapeau={[user?.username && `@${user.username}`, user?.email, membreDepuis && `membre depuis ${membreDepuis}`].filter(Boolean).join(' · ')}
    >

      {/* ── Alertes Telegram ──
          La saisie manuelle du Chat ID a été remplacée par un code à usage unique
          (2026-08-16) : sur 12 comptes, aucun n'avait réussi l'ancienne procédure. */}
      <section className="zk-carte zk-prf-section" aria-labelledby="prf-telegram">
        <h2 id="prf-telegram">Alertes Telegram</h2>
        <Message m={msgTelegram} />
        {telegramLie ? (
          <>
            <p><b className="zk-prf-ok">Telegram connecté.</b> Vous recevez les nouvelles bonnes affaires qui correspondent à vos critères.</p>
            <p className="zk-prf-muet">
              Réglez-les dans le bot avec /preferences, ou dans <Link to="/alerts" className="zk-lien">Mes alertes</Link>.
            </p>
            <button type="button" className="zk-bouton second" onClick={delierTelegram}>Délier ce compte Telegram</button>
          </>
        ) : !codeTelegram ? (
          <>
            <p className="zk-prf-muet">
              Recevez une alerte dès qu’une bonne affaire correspond à vos critères. Générez un code, puis tapez-le dans le bot.
            </p>
            <button type="button" className="zk-bouton" onClick={genererCode} disabled={telegramChargement}>
              {telegramChargement ? 'Génération…' : 'Générer un code Telegram'}
            </button>
          </>
        ) : (
          <>
            <ol className="zk-prf-etapes">
              <li>Ouvrez le bot <b>@{codeTelegram.bot_username}</b> sur Telegram</li>
              <li>Envoyez-lui cette commande :</li>
            </ol>
            <code className="zk-prf-commande">{codeTelegram.command}</code>
            <p className="zk-prf-muet">Code valable {codeTelegram.ttl_minutes} minutes. Une fois lié, rechargez cette page.</p>
          </>
        )}
      </section>

      {/* ── Informations personnelles ── */}
      <section className="zk-carte zk-prf-section" aria-labelledby="prf-infos">
        <h2 id="prf-infos">Informations personnelles</h2>
        <form onSubmit={enregistrer}>
          <div className="zk-champ">
            <label htmlFor="prf-nom">Nom complet</label>
            <input id="prf-nom" autoComplete="name" placeholder="Jean Dupont" value={form.full_name}
              onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))} />
          </div>
          <div className="zk-champ">
            <label htmlFor="prf-tel">Téléphone</label>
            <input id="prf-tel" type="tel" autoComplete="tel" placeholder="+33 6 12 34 56 78" value={form.phone}
              onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} />
          </div>
          <Message m={msgInfos} />
          <button type="submit" className="zk-bouton" disabled={enregistrement}>
            {enregistrement ? 'Sauvegarde…' : 'Enregistrer'}
          </button>
        </form>
      </section>

      {/* ── Sécurité / 2FA ── */}
      <section className="zk-carte zk-prf-section" aria-labelledby="prf-securite">
        <div className="zk-prf-tete">
          <h2 id="prf-securite">Double authentification</h2>
          <span className={`zk-prf-etat${actif2fa ? ' on' : ''}`}>{actif2fa ? 'Activée' : 'Désactivée'}</span>
        </div>
        <p className="zk-prf-muet">Un code à 6 chiffres généré par votre téléphone, demandé à chaque connexion.</p>
        <Message m={msg2fa} />

        {!actif2fa && !config2fa && (
          <button type="button" className="zk-bouton" onClick={demarrer2fa} disabled={occupe2fa}>
            {occupe2fa ? 'Préparation…' : 'Activer la double authentification'}
          </button>
        )}

        {!actif2fa && config2fa && (
          <form onSubmit={confirmer2fa}>
            <p><b>1. Scannez ce QR code avec votre application d’authentification</b></p>
            {config2fa.qr && <img className="zk-prf-qr" src={config2fa.qr} alt="QR code de configuration de la double authentification" />}
            <p className="zk-prf-muet">
              Pas de caméra ? Choisissez « saisir une clé de configuration » dans l’application et recopiez cette clé :
            </p>
            <code className="zk-prf-cle">{config2fa.secret}</code>
            <div className="zk-champ" style={{ marginTop: 16 }}>
              <label htmlFor="prf-code-activation">2. Saisissez le code affiché par l’application</label>
              <CodeSixChiffres id="prf-code-activation" valeur={code2fa} onChange={setCode2fa} />
            </div>
            <div className="zk-prf-boutons">
              <button type="submit" className="zk-bouton" disabled={occupe2fa || code2fa.length !== 6}>
                {occupe2fa ? 'Vérification…' : 'Confirmer et activer'}
              </button>
              <button type="button" className="zk-bouton second" onClick={() => { setConfig2fa(null); setCode2fa(''); setMsg2fa(null); }}>
                Annuler
              </button>
            </div>
          </form>
        )}

        {actif2fa && !desactivation2fa && (
          <button type="button" className="zk-bouton second zk-prf-danger" onClick={() => { setDesactivation2fa(true); setCode2fa(''); setMsg2fa(null); }}>
            Désactiver
          </button>
        )}

        {actif2fa && desactivation2fa && (
          <form onSubmit={desactiver2fa}>
            <div className="zk-champ">
              <label htmlFor="prf-code-desactivation">Saisissez un code de votre application pour confirmer la désactivation</label>
              <CodeSixChiffres id="prf-code-desactivation" valeur={code2fa} onChange={setCode2fa} />
            </div>
            <div className="zk-prf-boutons">
              <button type="submit" className="zk-bouton second zk-prf-danger" disabled={occupe2fa || code2fa.length !== 6}>
                {occupe2fa ? 'Vérification…' : 'Confirmer la désactivation'}
              </button>
              <button type="button" className="zk-bouton second" onClick={() => { setDesactivation2fa(false); setCode2fa(''); }}>
                Annuler
              </button>
            </div>
          </form>
        )}
      </section>

      <BandeauAffaires titre="Les bonnes affaires du moment" />

    </Page>
  );
}
